import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { chf } from "@/lib/format";
import { PageHeader, Loader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Store, Plus, Heart, Trash2, ExternalLink, MapPin, Star } from "lucide-react";

const STATUSES = { idea: "Idée", contacted: "Contacté", meeting: "Rendez-vous", "quote requested": "Devis demandé",
  "quote received": "Devis reçu", shortlisted: "Présélectionné", booked: "Réservé", rejected: "Écarté" };

export default function Vendors() {
  const { wedding } = useAuth();
  const wid = wedding?.wedding?.id;
  const [tab, setTab] = useState("mine");
  const [pv, setPv] = useState(null);
  const [favs, setFavs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ business_name: "", category: "Autre", contact_person: "", email: "", phone: "", status: "idea" });

  const load = async () => {
    const { data } = await api.get(`/weddings/${wid}/personal-vendors`); setPv(data);
    const f = await api.get(`/weddings/${wid}/favorites`); setFavs(f.data);
  };
  useEffect(() => { if (wid) load(); }, [wid]);

  const add = async () => {
    try { await api.post(`/weddings/${wid}/personal-vendors`, form); toast.success("Prestataire ajouté");
      setOpen(false); setForm({ business_name: "", category: "Autre", contact_person: "", email: "", phone: "", status: "idea" }); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const setStatus = async (v, status) => { await api.patch(`/weddings/${wid}/personal-vendors/${v.id}`, { status }); load(); };
  const del = async (id) => { await api.delete(`/weddings/${wid}/personal-vendors/${id}`); load(); };
  const unfav = async (vid) => { await api.post(`/weddings/${wid}/favorites/${vid}`); load(); };

  if (!pv) return <Loader />;

  return (
    <div>
      <PageHeader eyebrow="Prestataires" title="Vos prestataires"
        actions={<>
          <Link to="/marketplace"><Button variant="outline"><Store className="w-4 h-4 mr-1" /> Marketplace</Button></Link>
          <Button onClick={() => setOpen(true)} data-testid="add-vendor-btn" className="rounded-full"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
        </>} />

      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab("mine")} className={`px-4 py-1.5 rounded-full text-sm border ${tab === "mine" ? "bg-stone-900 text-white" : "bg-white"}`}>Mes prestataires ({pv.length})</button>
        <button onClick={() => setTab("favs")} className={`px-4 py-1.5 rounded-full text-sm border ${tab === "favs" ? "bg-stone-900 text-white" : "bg-white"}`}>Favoris ({favs.length})</button>
      </div>

      {tab === "mine" ? (
        pv.length === 0 ? <EmptyState icon={Store} title="Aucun prestataire" description="Ajoutez vos prestataires (même hors marketplace) et suivez leur statut."
          action={<Button onClick={() => setOpen(true)} className="rounded-full"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>} /> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {pv.map((v) => (
              <div key={v.id} className="card-lux p-5" data-testid={`pv-${v.id}`}>
                <div className="flex items-start justify-between">
                  <div><div className="font-serif text-lg">{v.business_name}</div>
                    <div className="text-xs text-stone-400">{v.category}</div></div>
                  <button onClick={() => del(v.id)} className="text-stone-300 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
                {v.contact_person && <div className="text-sm text-stone-500 mt-2">{v.contact_person}</div>}
                <select value={v.status} onChange={(e) => setStatus(v, e.target.value)}
                  className="mt-3 h-9 w-full rounded-md border px-2 text-sm" data-testid={`pv-status-${v.id}`}>
                  {Object.entries(STATUSES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
              </div>
            ))}
          </div>
        )
      ) : (
        favs.length === 0 ? <EmptyState icon={Heart} title="Aucun favori" description="Enregistrez des prestataires depuis la marketplace pour les comparer ici." /> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {favs.map(({ vendor: v, favorite_id }) => (
              <div key={favorite_id} className="card-lux overflow-hidden">
                <img src={v.cover_image || "https://images.unsplash.com/photo-1519741497674-611481863552?w=940&q=80"} alt={v.business_name} className="w-full h-36 object-cover" />
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="font-serif text-lg">{v.business_name}</div>
                    <button onClick={() => unfav(v.id)}><Heart className="w-4 h-4 fill-blush text-blush" /></button></div>
                  <div className="text-xs text-stone-400 flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" /> {v.city} · <Star className="w-3 h-3 fill-champagne text-champagne" /> {v.rating}</div>
                  <div className="text-sm mt-2">Dès {chf(v.starting_price)}</div>
                  <Link to={`/marketplace/${v.slug}`}><Button size="sm" variant="outline" className="w-full mt-3 rounded-full">Voir le profil <ExternalLink className="w-3 h-3 ml-1" /></Button></Link>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>Nouveau prestataire</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nom de l'entreprise</Label><Input data-testid="pv-name" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="mt-1 h-10" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Catégorie</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 h-10" /></div>
              <div><Label>Contact</Label><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="mt-1 h-10" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>E-mail</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 h-10" /></div>
              <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 h-10" /></div>
            </div>
          </div>
          <DialogFooter><Button data-testid="save-pv" onClick={add} className="rounded-full">Ajouter</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
